import { AxiosResponse } from "axios";
import { BillingDetails } from "../models/billing.details";
import { BillingUser } from "../models/billing.user";
import { BillingTable } from "../models/billing.table";
import { BillingCurrentPlan } from "../models/billing.current.plan";

import API from "../api";

const endpoint = "billings";

export interface PaymentMethodData {
  paymentMethodId: string;
  companyId: number;
  nameOnCard: string;
  billingAddress: string;
}

export interface PaymentMethod {
  id: number;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  stripePaymentMethodId?: string;
}

export interface AttachPaymentMethodResponse {
  success: boolean;
  paymentMethod: PaymentMethod;
}

export interface CreateCustomerData {
  id: number;
  name: string;
  email: string;
  profileId: number;
}

const BillingService = {
  // Métodos antigos (já existentes)
  async getDetails(): Promise<AxiosResponse<BillingDetails>> {
    return await API.get(`/${endpoint}/details`);
  },
  async getCurrentPlan(): Promise<AxiosResponse<BillingCurrentPlan>> {
    return await API.get(`/${endpoint}/current/plan`);
  },
  async getUsers(): Promise<AxiosResponse<BillingUser[]>> {
    return await API.get(`/${endpoint}/users`);
  },
  async getCartons(): Promise<AxiosResponse<BillingTable[]>> {
    return await API.get(`/${endpoint}/cartons`);
  },
  async cancelSubscription(): Promise<AxiosResponse<void>> {
    return await API.post(`/${endpoint}/cancel/subscription`);
  },
  async exportUsers(): Promise<AxiosResponse<string>> {
    return await API.get(`/${endpoint}/export/users`);
  },

  // Métodos novos (Payment Method - Gateway)
  async createCustomer(companyData: CreateCustomerData, userLoggedId?: number): Promise<{ success: boolean; customerId: string }> {
    const response = await API.post("/billing/customer/signUp", {
      ...companyData,
      userLoggedId,
    });
    return response.data;
  },

  async createSetupIntent(companyId: number): Promise<{ clientSecret: string }> {
    const response = await API.post("/billing/setup-intent/create", { companyId });

    // Backend retorna o SetupIntent completo, precisamos extrair o client_secret
    if (response.data) {
      // Verifica se response.data já é o objeto SetupIntent
      if (response.data.client_secret) {
        return { clientSecret: response.data.client_secret };
      }
      
      // Verifica se response.data.data existe (resposta aninhada)
      if (response.data.data && response.data.data.client_secret) {
        return { clientSecret: response.data.data.client_secret };
      }
      
      // Fallback: se não tiver client_secret, pode ser que já esteja no formato correto
      if (typeof response.data === 'string') {
        return { clientSecret: response.data };
      }
    }

    console.error("Invalid response format:", response.data);
    throw new Error("Invalid response format: missing client_secret");
  },

  async attachPaymentMethod(data: PaymentMethodData): Promise<AttachPaymentMethodResponse> {
    const response = await API.post("/billing/payment-method/attach", data);
    return response.data;
  },

  async listPaymentMethods(companyId: number): Promise<PaymentMethod[]> {
    const response = await API.post("/billing/payment-method/list", { companyId });
    return response.data;
  },

  async getDefaultPaymentMethod(companyId: number): Promise<PaymentMethod | null> {
    const response = await API.get(`/billing/payment-method/default`, {
      params: { companyId },
    });
    return response.data;
  },

  async removePaymentMethod(paymentMethodId: number, companyId: number): Promise<{ success: boolean }> {
    const response = await API.post("/billing/payment-method/remove", {
      paymentMethodId,
      companyId,
    });
    return response.data;
  },

  async setDefaultPaymentMethod(paymentMethodId: number, companyId: number): Promise<{ success: boolean }> {
    const response = await API.post("/billing/payment-method/set-default", {
      paymentMethodId,
      companyId,
    });
    return response.data;
  },
};

export default BillingService;
